import { addMonths, addYears, getMonth, getYear } from 'date-fns';

import { CdkAriaLive, CdkMonitorFocus } from '@angular/cdk/a11y';
import { ComponentType } from '@angular/cdk/portal';
import {
    AfterContentInit,
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    HostBinding,
    inject,
    input,
    OnChanges,
    OnDestroy,
    output,
    SimpleChanges,
    viewChild
} from '@angular/core';
import { Subject } from 'rxjs';

import { ApDateService } from '@ap/date';
import { ApIconComponent } from '@ap/icon';

import type { ApCalendarView } from '../date-input.model';

import { ApMonthViewComponent } from './month-view/month-view.component';
import { ApMultiYearViewComponent, yearsPerPage } from './multi-year-view/multi-year-view.component';
import { ApYearViewComponent } from './year-view/year-view.component';

/**
 * This is the Calendar Component. A calendar is used as part of the datepicker.
 * There are different views available for the calendar: Month, year or multi-year.
 **/
@Component({
    selector: 'ap-calendar',
    templateUrl: 'calendar.component.html',
    exportAs: 'apCalendar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        forwardRef(() => ApCalendarHeaderComponent),
        CdkMonitorFocus,
        ApMonthViewComponent,
        ApYearViewComponent,
        ApMultiYearViewComponent
    ]
})
export class ApCalendarComponent implements AfterContentInit, AfterViewChecked, OnDestroy, OnChanges {
    @HostBinding('class.ap-calendar') apCalendarClass = true;

    /** A date representing the period (month or year) to start the calendar in. */
    public readonly startAt = input(undefined, {
        transform: (value: Date | null): Date | null => {
            return ApDateService.getValidDateOrNull(ApDateService.parse(value));
        }
    });

    /** The currently selected date. */
    public readonly selected = input(undefined, {
        transform: (value: Date | null): Date | null => {
            return ApDateService.getValidDateOrNull(ApDateService.utc(value));
        }
    });

    /** The minimum selectable date. */
    public readonly minDate = input(undefined, {
        transform: (value: Date | null): Date | null => {
            return ApDateService.getValidDateOrNull(ApDateService.utc(value));
        }
    });

    /** The maximum selectable date. */
    public readonly maxDate = input(undefined, {
        transform: (value: Date | null): Date | null => {
            return ApDateService.getValidDateOrNull(ApDateService.utc(value));
        }
    });

    /**
     * The current active date. This determines which time period is shown and which date is
     * highlighted when using keyboard navigation.
     */
    get activeDate(): Date {
        return this._clampedActiveDate;
    }

    set activeDate(value: Date) {
        this._clampedActiveDate = ApDateService.clampDate(value, this.minDate(), this.maxDate());
        this.stateChanges.next();
    }

    /** Whether the calendar is in month view. */
    get currentView(): ApCalendarView {
        return this._currentView;
    }

    set currentView(value: ApCalendarView) {
        this._currentView = value;
        this._moveFocusOnNextTick = true;
        this.stateChanges.next();
    }
    /** An input indicating the type of the header component, if set. */
    readonly headerComponent = input<ComponentType<any>>(undefined);

    /** Whether the calendar should be started in month or year view. */
    readonly startView = input<ApCalendarView>('month');
    /** A function used to filter which dates are selectable. */
    readonly dateFilter = input<(date: Date) => boolean>(undefined);
    /** Emits when the currently selected date changes. */
    readonly selectedChange = output<Date>();
    /**
     * Emits the year chosen in multiyear view.
     * This doesn't imply a change on the selected date.
     */
    readonly yearSelected = output<Date>();
    /**
     * Emits the month chosen in year view.
     * This doesn't imply a change on the selected date.
     */
    readonly monthSelected = output<Date>();
    /** Emits when any date is selected. */
    readonly _userSelection = output<void>();
    /** Reference to the current month view component. */
    readonly monthView = viewChild(ApMonthViewComponent);
    /** Reference to the current year view component. */
    readonly yearView = viewChild(ApYearViewComponent);
    /** Reference to the current multi-year view component. */
    readonly multiYearView = viewChild(ApMultiYearViewComponent);
    /**
     * Emits whenever there is a state change that the header may need to respond to.
     */
    stateChanges = new Subject<void>();
    /**
     * Used for scheduling that focus should be moved to the active cell on the next tick.
     * We need to schedule it, rather than do it immediately, because we have to wait
     * for Angular to re-evaluate the view children.
     */
    private _moveFocusOnNextTick = false;
    private _clampedActiveDate: Date;

    private _currentView: ApCalendarView;

    ngAfterContentInit() {
        this.activeDate = this.startAt() || ApDateService.utc(new Date());

        // Assign to the private property since we don't want to move focus on init.
        this._currentView = this.startView();
    }

    ngAfterViewChecked() {
        if (this._moveFocusOnNextTick) {
            this._moveFocusOnNextTick = false;
            this.focusActiveCell();
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        const change = changes.minDate || changes.maxDate || changes.dateFilter;

        if (change && !change.firstChange) {
            const view = this._getCurrentViewComponent();

            if (view) {
                view._init();
            }
        }
    }

    focusActiveCell() {
        this._getCurrentViewComponent()._focusActiveCell();
    }

    /** Handles date selection in the month view. */
    _dateSelected(date: Date): void {
        if (!date || (!this.selected() && date !== this.selected()) || date.toJSON() !== this.selected().toJSON()) {
            this.selectedChange.emit(date);
        }
    }

    /** Handles year selection in the multiyear view. */
    _yearSelectedInMultiYearView(normalizedYear: Date) {
        this.yearSelected.emit(normalizedYear);
    }

    /** Handles month selection in the year view. */
    _monthSelectedInYearView(normalizedMonth: Date) {
        this.monthSelected.emit(normalizedMonth);
    }

    _userSelected(): void {
        this._userSelection.emit(undefined);
    }

    /** Handles year/month selection in the multi-year/year views. */
    _goToDateInView(date: Date, view: ApCalendarView): void {
        this.activeDate = date;
        this.currentView = view;
    }

    /** Returns the component instance that corresponds to the current calendar view. */
    private _getCurrentViewComponent() {
        return this.monthView() || this.yearView() || this.multiYearView();
    }
}

/** Default header for MatCalendar */
@Component({
    selector: 'ap-calendar-header',
    templateUrl: './calendar-header.component.html',
    styleUrls: ['./calendar-header.component.scss'],
    exportAs: 'apCalendarHeader',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CdkMonitorFocus, ApIconComponent, CdkAriaLive]
})
export class ApCalendarHeaderComponent {
    calendar = inject(ApCalendarComponent);

    constructor() {
        const changeDetectorRef = inject(ChangeDetectorRef);

        this.calendar.stateChanges.subscribe(() => changeDetectorRef.markForCheck());
    }

    /** The label for the current calendar view. */
    get periodButtonText(): string {
        if (this.calendar.currentView === 'month') {
            const month = ApDateService.format(this.calendar.activeDate, 'MMMM yyyy');
            return month.charAt(0).toUpperCase() + month.slice(1);
        }
        if (this.calendar.currentView === 'year') {
            return ApDateService.format(this.calendar.activeDate, 'yyyy');
        }
        const activeYear = getYear(this.calendar.activeDate);

        const firstYearInView = getYear(ApDateService.utc(new Date(activeYear - (activeYear % 24), 0, 1)));
        const lastYearInView = getYear(ApDateService.utc(new Date(activeYear + yearsPerPage - 1 - (activeYear % 24), 0, 1)));

        return `${firstYearInView} \u2013 ${lastYearInView}`;
    }

    get periodButtonLabel(): string {
        return this.calendar.currentView === 'month' ? 'Kies een maand' : 'Kies een jaar'; // TODO: content
        // this._intl.switchToMultiYearViewLabel : this._intl.switchToMonthViewLabel;
    }

    /** The label for the the previous button. */
    get prevButtonLabel(): string {
        return {
            month: 'vorige maand',
            year: 'vorig jaar',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'multi-year': 'vorige 24 jaar'
        }[this.calendar.currentView];
    }

    /** The label for the the next button. */
    get nextButtonLabel(): string {
        return {
            month: 'volgende maand',
            year: 'volgend jaar',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'multi-year': 'volgende 24 jaar'
        }[this.calendar.currentView];
    }

    /** Handles user clicks on the period label. */
    currentPeriodClicked(): void {
        this.calendar.currentView = this.calendar.currentView === 'month' ? 'year' : 'multi-year';
    }

    /** Handles user clicks on the previous button. */
    previousClicked(): void {
        this.calendar.activeDate =
            this.calendar.currentView === 'month'
                ? addMonths(this.calendar.activeDate, -1)
                : addYears(this.calendar.activeDate, this.calendar.currentView === 'year' ? -1 : -yearsPerPage);
    }

    /** Handles user clicks on the next button. */
    nextClicked(): void {
        this.calendar.activeDate =
            this.calendar.currentView === 'month'
                ? addMonths(this.calendar.activeDate, 1)
                : addYears(this.calendar.activeDate, this.calendar.currentView === 'year' ? 1 : yearsPerPage);
    }

    /** Whether the previous period button is enabled. */
    previousEnabled(): boolean {
        if (!this.calendar.minDate()) {
            return true;
        }
        return !this.calendar.minDate() || !this._isSameView(this.calendar.activeDate, this.calendar.minDate());
    }

    /** Whether the next period button is enabled. */
    nextEnabled(): boolean {
        return !this.calendar.maxDate() || !this._isSameView(this.calendar.activeDate, this.calendar.maxDate());
    }

    /** Whether the two dates represent the same view in the current view mode (month or year). */
    private _isSameView(date1: Date, date2: Date): boolean {
        if (this.calendar.currentView === 'month') {
            return getYear(date1) === getYear(date2) && getMonth(date1) === getMonth(date2);
        }
        if (this.calendar.currentView === 'year') {
            return getYear(date1) === getYear(date2);
        }
        // Otherwise we are in 'multi-year' view.
        return Math.floor(getYear(date1) / yearsPerPage) === Math.floor(getYear(date2) / yearsPerPage);
    }
}
